/* ==========================================================================
   Côte Dorée Theme - JavaScript (vanilla, no dependencies)
   ========================================================================== */
(() => {
  'use strict';

  const T = window.theme || {};
  const routes = T.routes || {};
  const strings = T.strings || {};
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const pad = (n) => String(n).padStart(2, '0');
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const safeStorage = {
    get(store, key) { try { return window[store].getItem(key); } catch (e) { return null; } },
    set(store, key, value) { try { window[store].setItem(key, value); } catch (e) { /* storage blocked */ } },
    remove(store, key) { try { window[store].removeItem(key); } catch (e) { /* storage blocked */ } }
  };

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(message) {
    const el = qs('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 4500);
  }

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    format = format || T.moneyFormat || '{{amount}}';
    cents = Number(cents) || 0;
    const place = (precision, thousands, decimal) => {
      const fixed = (cents / 100).toFixed(precision).split('.');
      const whole = fixed[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return fixed[1] ? whole + decimal + fixed[1] : whole;
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    let value;
    switch (match && match[1]) {
      case 'amount_no_decimals': value = place(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = place(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = place(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = place(2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = place(0, ' ', '.'); break;
      case 'amount_with_space_separator': value = place(2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = place(2, ' ', '.'); break;
      default: value = place(2, ',', '.');
    }
    return format.replace(match ? match[0] : '{{amount}}', value);
  }

  /* Whole amounts without decimals (for example $23), otherwise the normal format */
  function formatMoneyShort(cents) {
    const format = T.moneyFormat || '{{amount}}';
    if (Number(cents) % 100 !== 0) return formatMoney(cents, format);
    const map = {
      amount: 'amount_no_decimals',
      amount_with_comma_separator: 'amount_no_decimals_with_comma_separator',
      amount_with_space_separator: 'amount_no_decimals_with_space_separator'
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match || !map[match[1]]) return formatMoney(cents, format);
    return formatMoney(cents, format.replace(match[0], '{{' + map[match[1]] + '}}'));
  }

  /* ---------- Drawers (mobile menu, search, cart) ---------- */
  let activeDrawer = null;
  let lastTrigger = null;

  function focusableIn(root) {
    return qsa('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', root)
      .filter((el) => el.offsetParent !== null);
  }

  function openDrawer(drawer) {
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(activeDrawer, false);
    lastTrigger = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('has-drawer');
    activeDrawer = drawer;
    setTimeout(() => {
      const target = qs('[data-autofocus]', drawer) || focusableIn(qs('.drawer__panel', drawer) || drawer)[0];
      if (target) target.focus();
    }, 80);
  }

  function closeDrawer(drawer, restoreFocus = true) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (activeDrawer === drawer) activeDrawer = null;
    if (!activeDrawer) document.documentElement.classList.remove('has-drawer');
    if (restoreFocus && lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
  }

  /* ---------- Cart ---------- */
  const cartDrawerEl = () => qs('[data-cart-drawer]');
  const isCartPage = () => !!qs('[data-cart-page]');

  function updateCartCount(count) {
    qsa('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = !count;
    });
  }

  async function refreshCart() {
    const drawer = cartDrawerEl();
    const requests = [fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json())];
    if (drawer) requests.push(fetch(routes.root + '?sections=cart-drawer').then((r) => r.json()));
    const [cart, sections] = await Promise.all(requests);
    updateCartCount(cart.item_count);

    if (drawer && sections && sections['cart-drawer']) {
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = qs('[data-cart-drawer-inner]', doc);
      const current = qs('[data-cart-drawer-inner]', drawer);
      if (fresh && current) {
        const scroller = qs('.cart-drawer__scroll', current);
        const scrollTop = scroller ? scroller.scrollTop : 0;
        current.replaceWith(fresh);
        const freshScroller = qs('.cart-drawer__scroll', fresh);
        if (freshScroller) freshScroller.scrollTop = scrollTop;
      }
      initCartTimer();
    }
    return cart;
  }

  function showCartError(message) {
    const el = qs('[data-cart-error]');
    if (!el) { toast(message); return; }
    el.textContent = message;
    el.hidden = false;
  }

  async function changeLine(key, quantity, input) {
    const inner = qs('[data-cart-drawer-inner]');
    if (inner) inner.classList.add('is-updating');
    try {
      const res = await fetch(routes.cart_change + '.js', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ id: key, quantity })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.error);
      if (isCartPage()) { window.location.reload(); return; }
      await refreshCart();
    } catch (error) {
      if (input) input.value = input.defaultValue;
      if (!isCartPage()) await refreshCart().catch(() => {});
      showCartError(error.message || strings.error);
    } finally {
      const currentInner = qs('[data-cart-drawer-inner]');
      if (currentInner) currentInner.classList.remove('is-updating');
    }
  }

  async function addItems(items) {
    const res = await fetch(routes.cart_add + '.js', {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ items })
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || strings.error);
    return data;
  }

  async function afterAdd() {
    if (T.cartType === 'drawer' && cartDrawerEl()) {
      await refreshCart();
      openDrawer(cartDrawerEl());
    } else {
      window.location.href = routes.cart;
    }
  }

  function setLoading(button, loading) {
    if (!button) return;
    const label = qs('[data-button-label]', button);
    button.classList.toggle('is-loading', loading);
    if (label) {
      if (loading) {
        label.dataset.original = label.textContent;
        label.textContent = strings.adding;
      } else if (label.dataset.original) {
        label.textContent = label.dataset.original;
      }
    }
  }

  /* Cart timer */
  let cartTimerInterval = null;
  function initCartTimer() {
    clearInterval(cartTimerInterval);
    const storageKey = 'theme:cartTimerEnd';
    if (qs('[data-cart-empty]')) safeStorage.remove('sessionStorage', storageKey);
    const el = qs('[data-cart-timer]');
    if (!el) return;
    const minutes = parseInt(el.dataset.minutes, 10) || 5;
    const display = qs('[data-cart-timer-display]', el);
    let end = parseInt(safeStorage.get('sessionStorage', storageKey) || '0', 10);
    if (!end) {
      end = Date.now() + minutes * 60000;
      safeStorage.set('sessionStorage', storageKey, String(end));
    }
    const tick = () => {
      let left = end - Date.now();
      if (left <= 0) {
        if (T.cartTimerRestart) {
          end = Date.now() + minutes * 60000;
          safeStorage.set('sessionStorage', storageKey, String(end));
          left = end - Date.now();
        } else {
          el.hidden = true;
          clearInterval(cartTimerInterval);
          return;
        }
      }
      const total = Math.ceil(left / 1000);
      display.textContent = pad(Math.floor(total / 60)) + ':' + pad(total % 60);
    };
    tick();
    cartTimerInterval = setInterval(tick, 1000);
  }

  /* ---------- Announcement countdown ---------- */
  function initCountdowns(scope = document) {
    qsa('[data-countdown]', scope).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      const mode = el.dataset.mode;
      const bar = el.closest('[data-announcement]');
      const parts = {
        days: qs('[data-days]', el),
        hours: qs('[data-hours]', el),
        mins: qs('[data-mins]', el),
        secs: qs('[data-secs]', el)
      };

      const evergreenEnd = () => {
        const duration = (parseFloat(el.dataset.evergreenHours) || 24) * 3600000;
        const key = 'theme:countdown:' + (el.dataset.id || 'a');
        let start = parseInt(safeStorage.get('localStorage', key) || '0', 10);
        if (!start || Date.now() - start >= duration) {
          start = Date.now();
          safeStorage.set('localStorage', key, String(start));
        }
        return start + duration;
      };

      let end = mode === 'evergreen' ? evergreenEnd() : Date.parse(el.dataset.end);
      if (Number.isNaN(end)) { el.hidden = true; return; }

      const render = () => {
        let diff = end - Date.now();
        if (diff <= 0) {
          if (mode === 'evergreen') {
            end = evergreenEnd();
            diff = end - Date.now();
          } else {
            el.hidden = true;
            const sub = bar && qs('.announcement__sub', bar);
            if (sub) sub.hidden = true;
            if (bar && bar.dataset.hideWhenEnded === 'true') bar.hidden = true;
            return false;
          }
        }
        const total = Math.floor(diff / 1000);
        parts.days.textContent = pad(Math.floor(total / 86400));
        parts.hours.textContent = pad(Math.floor((total % 86400) / 3600));
        parts.mins.textContent = pad(Math.floor((total % 3600) / 60));
        parts.secs.textContent = pad(total % 60);
        return true;
      };

      if (render()) {
        const id = setInterval(() => { if (!render()) clearInterval(id); }, 1000);
      }
    });
  }

  /* ---------- Gallery ---------- */
  function initGallery(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const viewport = qs('[data-gallery-viewport]', root);
    const slides = qsa('[data-gallery-slide]', root);
    const thumbsWrap = qs('[data-gallery-thumbs]', root);
    const thumbs = qsa('[data-gallery-thumb]', root);
    const prev = qs('[data-gallery-prev]', root);
    const next = qs('[data-gallery-next]', root);
    if (!viewport || !slides.length) return;
    let index = 0;

    const setActive = (i) => {
      index = i;
      thumbs.forEach((thumb, n) => {
        thumb.classList.toggle('is-active', n === i);
        if (n === i) thumb.setAttribute('aria-current', 'true'); else thumb.removeAttribute('aria-current');
      });
      if (thumbsWrap && thumbs[i]) {
        const li = thumbs[i].parentElement;
        thumbsWrap.scrollTo({ left: li.offsetLeft - (thumbsWrap.clientWidth - li.offsetWidth) / 2, behavior: 'smooth' });
      }
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    };

    const goTo = (i, smooth = true) => {
      i = Math.max(0, Math.min(slides.length - 1, i));
      viewport.scrollTo({ left: i * viewport.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      setActive(i);
    };

    let frame;
    viewport.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const i = Math.round(viewport.scrollLeft / (viewport.clientWidth || 1));
        if (i !== index) setActive(i);
      });
    }, { passive: true });

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.index))));
    if (prev) prev.addEventListener('click', () => goTo(index - 1));
    if (next) next.addEventListener('click', () => goTo(index + 1));
    window.addEventListener('resize', () => goTo(index, false));

    root.galleryGoToMedia = (mediaId) => {
      const i = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (i > -1) goTo(i);
    };
    setActive(0);
  }

  /* ---------- Carousel (reviews, media gallery) ---------- */
  function initCarousel(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const track = qs('[data-carousel-track]', root);
    const prev = qs('[data-carousel-prev]', root);
    const next = qs('[data-carousel-next]', root);
    if (!track) return;

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      root.classList.toggle('has-overflow', max > 2);
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    };
    const step = () => track.clientWidth * 0.9;
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    update();
  }

  /* Autoplaying videos only play while visible */
  function initVideoObserver(scope = document) {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) { const p = video.play(); if (p && p.catch) p.catch(() => {}); } else { video.pause(); }
      });
    }, { threshold: 0.25 });
    qsa('.media-tile__video[autoplay]', scope).forEach((video) => {
      if (video.dataset.observed) return;
      video.dataset.observed = '1';
      observer.observe(video);
    });
  }

  /* ---------- Product ---------- */
  function initProduct(root) {
    if (root.dataset.ready) return;
    const form = qs('[data-product-form]', root);
    if (!form) return;
    root.dataset.ready = '1';

    let variants = [];
    try { variants = JSON.parse(qs('[data-variants]', root).textContent); } catch (e) { variants = []; }
    const byId = new Map(variants.map((v) => [String(v.id), v]));
    const idInput = qs('[data-variant-id-input]', form);
    const addBtn = qs('[data-add-button]', form);
    const errorEl = qs('[data-product-error]', form);
    const priceWrap = qs('[data-price-wrapper]', form);
    const qtyInput = qs('[data-product-qty]', form);
    const optionEls = qsa('[data-option]', form);
    const offers = qsa('[data-offer]', form);
    const hasOffers = offers.length > 0;
    const gallery = qs('[data-gallery]', root);
    const sticky = qs('[data-sticky-atc]', root);
    const stickySelect = sticky && qs('[data-sticky-select]', sticky);
    const stickyBtn = sticky && qs('[data-sticky-add]', sticky);
    const stickyPrice = sticky && qs('[data-sticky-price]', sticky);
    let busy = false;

    const priceHTML = (v) => (v.compare_at_price > v.price
      ? `<div class="price price--on-sale"><s class="price__compare">${formatMoney(v.compare_at_price)}</s> <span class="price__current price__current--sale">${formatMoney(v.price)}</span></div>`
      : `<div class="price"><span class="price__current">${formatMoney(v.price)}</span></div>`);

    const setButtons = (available, unavailableLabel) => {
      [addBtn, stickyBtn].forEach((btn) => {
        if (!btn) return;
        btn.disabled = !available;
        const label = qs('[data-button-label]', btn);
        if (label && !btn.classList.contains('is-loading')) {
          label.textContent = available ? strings.addToCart : unavailableLabel;
        }
      });
    };

    const showError = (message) => {
      if (!errorEl) { toast(message); return; }
      errorEl.textContent = message;
      errorEl.hidden = false;
    };
    const hideError = () => { if (errorEl) errorEl.hidden = true; };

    /* --- Standard variant selection --- */
    const readOptions = () => optionEls.map((wrap) => {
      const checked = qs('input[type="radio"]:checked', wrap);
      if (checked) return checked.value;
      const select = qs('select', wrap);
      return select ? select.value : '';
    });

    const applyVariant = (variant) => {
      if (!variant) {
        setButtons(false, strings.unavailable);
        return;
      }
      idInput.value = variant.id;
      if (priceWrap) priceWrap.innerHTML = priceHTML(variant);
      if (stickyPrice) stickyPrice.innerHTML = priceHTML(variant);
      if (stickySelect) stickySelect.value = String(variant.id);
      setButtons(variant.available, strings.soldOut);
      if (gallery && gallery.galleryGoToMedia && variant.media_id) gallery.galleryGoToMedia(variant.media_id);
      if (window.history && history.replaceState && root.dataset.productUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    };

    const onOptionChange = () => {
      const options = readOptions();
      const variant = variants.find((v) => v.options.every((value, i) => value === options[i]));
      applyVariant(variant);
    };

    optionEls.forEach((wrap) => wrap.addEventListener('change', onOptionChange));

    /* --- Bundle offers --- */
    const selectedOffer = () => offers.find((o) => qs('[data-offer-input]', o).checked) || offers[0];
    const unitIds = (offer) => {
      const selects = qsa('[data-unit-select]', offer);
      if (selects.length) return selects.map((s) => s.value);
      const qty = parseInt(offer.dataset.quantity, 10) || 1;
      return Array(qty).fill(String(idInput.value));
    };

    const recalcOffer = (offer) => {
      const discount = parseFloat(offer.dataset.discount) || 0;
      const ids = unitIds(offer);
      const full = ids.reduce((sum, id) => sum + ((byId.get(String(id)) || {}).price || 0), 0);
      const total = Math.floor((full * (100 - discount)) / 100);
      const saving = full - total;
      const totalEl = qs('[data-offer-total]', offer);
      const compareEl = qs('[data-offer-compare]', offer);
      const saveEl = qs('[data-offer-save]', offer);
      if (totalEl) totalEl.innerHTML = formatMoneyShort(total);
      if (compareEl) { compareEl.innerHTML = formatMoneyShort(full); compareEl.hidden = discount <= 0; }
      if (saveEl) {
        saveEl.innerHTML = String(strings.youSave || '').replace('__AMOUNT__', discount + '%');
        saveEl.hidden = saving <= 0;
      }
    };

    const refreshOffers = () => {
      const current = selectedOffer();
      offers.forEach((offer) => offer.classList.toggle('is-selected', offer === current));
      offers.forEach(recalcOffer);
      const ids = unitIds(current);
      const allAvailable = ids.every((id) => (byId.get(String(id)) || {}).available);
      setButtons(allAvailable, strings.soldOut);
      const first = byId.get(String(ids[0]));
      if (first) {
        if (stickySelect) stickySelect.value = String(first.id);
        if (stickyPrice) {
          const discount = parseFloat(current.dataset.discount) || 0;
          const full = ids.reduce((sum, id) => sum + ((byId.get(String(id)) || {}).price || 0), 0);
          stickyPrice.innerHTML = `<div class="price"><span class="price__current">${formatMoney(Math.floor((full * (100 - discount)) / 100))}</span></div>`;
        }
        if (gallery && gallery.galleryGoToMedia && first.media_id) gallery.galleryGoToMedia(first.media_id);
      }
    };

    if (hasOffers) {
      if (!offers.some((o) => qs('[data-offer-input]', o).checked)) qs('[data-offer-input]', offers[0]).checked = true;
      offers.forEach((offer) => {
        offer.addEventListener('change', (event) => {
          if (event.target.matches('[data-offer-input], [data-unit-select]')) refreshOffers();
        });
      });
      refreshOffers();
    }

    /* --- Sticky bar variant select mirrors the main selection --- */
    if (stickySelect) {
      stickySelect.addEventListener('change', () => {
        const variant = byId.get(stickySelect.value);
        if (!variant) return;
        if (hasOffers) {
          const first = qs('[data-unit-select]', selectedOffer());
          if (first) { first.value = String(variant.id); first.dispatchEvent(new Event('change', { bubbles: true })); }
        } else {
          optionEls.forEach((wrap, i) => {
            const radios = qsa('input[type="radio"]', wrap);
            if (radios.length) radios.forEach((r) => { r.checked = r.value === variant.options[i]; });
            const select = qs('select', wrap);
            if (select) select.value = variant.options[i];
          });
          onOptionChange();
        }
      });
    }

    /* --- Submit --- */
    const collectItems = () => {
      const items = [];
      if (hasOffers) {
        const counts = new Map();
        unitIds(selectedOffer()).forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
        counts.forEach((quantity, id) => items.push({ id: Number(id), quantity }));
      } else {
        items.push({
          id: Number(idInput.value),
          quantity: Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1)
        });
      }
      qsa('[data-addon-input]:checked', root).forEach((addon) => {
        items.push({ id: Number(addon.dataset.variantId), quantity: 1 });
      });
      return items;
    };

    const submit = async () => {
      if (busy) return;
      busy = true;
      hideError();
      setLoading(addBtn, true);
      setLoading(stickyBtn, true);
      try {
        await addItems(collectItems());
        await afterAdd();
      } catch (error) {
        showError(error.message || strings.error);
      } finally {
        busy = false;
        setLoading(addBtn, false);
        setLoading(stickyBtn, false);
      }
    };

    form.addEventListener('submit', (event) => { event.preventDefault(); submit(); });
    if (stickyBtn) stickyBtn.addEventListener('click', submit);

    /* --- Sticky bar visibility --- */
    if (sticky && addBtn && 'IntersectionObserver' in window) {
      document.body.classList.add('has-sticky-atc');
      const observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        qsa('button, select', sticky).forEach((el) => { el.tabIndex = show ? 0 : -1; });
      });
      observer.observe(addBtn);
    }
  }

  /* ---------- Search (live suggestions) ---------- */
  function initSearch() {
    qsa('[data-search-root]').forEach((rootEl) => {
      if (rootEl.dataset.ready) return;
      rootEl.dataset.ready = '1';
      const input = qs('input[name="q"]', rootEl);
      const results = qs('[data-search-results]', rootEl);
      if (!input || !results || !T.predictive) return;
      let timer;
      let controller;

      input.addEventListener('input', () => {
        clearTimeout(timer);
        const term = input.value.trim();
        if (term.length < 2) {
          results.hidden = true;
          results.innerHTML = '';
          return;
        }
        timer = setTimeout(async () => {
          if (controller) controller.abort();
          controller = new AbortController();
          try {
            const url = `${routes.predictive_search}?q=${encodeURIComponent(term)}&resources[type]=product,collection&resources[limit]=6&section_id=predictive-search`;
            const res = await fetch(url, { signal: controller.signal });
            if (!res.ok) throw new Error('search failed');
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            const inner = qs('[data-predictive-inner]', doc);
            results.innerHTML = inner ? inner.innerHTML : '';
            results.hidden = !inner;
          } catch (error) {
            if (error.name !== 'AbortError') results.hidden = true;
          }
        }, 250);
      });
    });
  }

  /* ---------- Global events ---------- */
  document.addEventListener('click', (event) => {
    const target = event.target;

    const opener = target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(document.getElementById(opener.dataset.drawerOpen));
      return;
    }

    const closer = target.closest('[data-drawer-close]');
    if (closer) {
      const drawer = closer.closest('[data-drawer]');
      if (drawer) { event.preventDefault(); closeDrawer(drawer); }
      return;
    }

    const cartOpen = target.closest('[data-cart-open]');
    if (cartOpen && T.cartType === 'drawer' && cartDrawerEl()) {
      event.preventDefault();
      openDrawer(cartDrawerEl());
      return;
    }

    const quick = target.closest('[data-add-to-cart]');
    if (quick) {
      event.preventDefault();
      if (quick.classList.contains('is-loading')) return;
      setLoading(quick, true);
      addItems([{ id: Number(quick.dataset.variantId), quantity: 1 }])
        .then(afterAdd)
        .catch((error) => toast(error.message || strings.error))
        .finally(() => setLoading(quick, false));
      return;
    }

    const minus = target.closest('[data-qty-minus]');
    const plus = target.closest('[data-qty-plus]');
    if (minus || plus) {
      const wrap = (minus || plus).closest('[data-qty]');
      const input = wrap && qs('input', wrap);
      if (!input) return;
      event.preventDefault();
      const min = parseInt(input.min || '0', 10);
      let value = (parseInt(input.value, 10) || 0) + (plus ? 1 : -1);
      if (value < min) value = min;
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const remove = target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.dataset.key, 0);
      return;
    }

    const sound = target.closest('[data-video-sound]');
    if (sound) {
      const video = qs('video', sound.closest('.media-tile'));
      if (video) {
        video.muted = !video.muted;
        if (!video.muted && video.paused) video.play();
        sound.setAttribute('aria-pressed', String(!video.muted));
        qs('[data-sound-off]', sound).hidden = !video.muted;
        qs('[data-sound-on]', sound).hidden = video.muted;
      }
      return;
    }

    qsa('.facet[open]').forEach((facet) => { if (!facet.contains(target)) facet.removeAttribute('open'); });
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target.matches('[data-cart-qty]')) {
      const value = Math.max(0, parseInt(target.value, 10) || 0);
      changeLine(target.dataset.key, value, target);
    } else if (target.matches('[data-cart-note]')) {
      fetch(routes.cart_update + '.js', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ note: target.value })
      }).catch(() => {});
    } else if (target.matches('[data-localization-select]')) {
      target.closest('form').submit();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (activeDrawer) closeDrawer(activeDrawer);
      qsa('.facet[open]').forEach((facet) => facet.removeAttribute('open'));
      return;
    }
    if (event.key === 'Enter' && event.target.matches('[data-cart-qty]')) {
      event.preventDefault();
      event.target.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    if (event.key === 'Tab' && activeDrawer) {
      const items = focusableIn(qs('.drawer__panel', activeDrawer) || activeDrawer);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });

  /* Collection filters and sorting submit automatically */
  function initFilters(scope = document) {
    qsa('[data-filter-form]', scope).forEach((form) => {
      if (form.dataset.ready) return;
      form.dataset.ready = '1';
      form.addEventListener('change', () => { if (form.requestSubmit) form.requestSubmit(); else form.submit(); });
      form.addEventListener('submit', () => {
        qsa('input', form).forEach((input) => {
          if (input.type !== 'checkbox' && input.value === '') { input.disabled = true; input.dataset.tempDisabled = '1'; }
        });
      });
    });
  }
  window.addEventListener('pageshow', (event) => {
    qsa('[data-temp-disabled]').forEach((input) => { input.disabled = false; delete input.dataset.tempDisabled; });
    if (event.persisted) refreshCart().catch(() => {});
  });

  /* ---------- Init ---------- */
  function initAll(scope = document) {
    qsa('[data-gallery]', scope).forEach(initGallery);
    qsa('[data-carousel]', scope).forEach(initCarousel);
    qsa('[data-product-section]', scope).forEach(initProduct);
    initCountdowns(scope);
    initVideoObserver(scope);
    initFilters(scope);
    initSearch();
    initCartTimer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  /* Theme editor: re-initialise sections after they are edited */
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();